/**
 * Healix Variant Sheet — shared variant-picker bottom sheet (mobile) /
 * centered modal (desktop) for one or more products.
 *
 * Public API (the ONLY entry point — nothing auto-mounts):
 *   window.HealixVariantSheet.open({
 *     items: [{ handle, title?, imageUrl?, price? }, ...],
 *     heading?: 'Complete the set'
 *   }) -> Promise<{ added: [{ handle, variantId, quantity }, ...] }>
 *
 * Resolves { added: [...] } after a successful cart add; resolves
 * { added: [] } when the sheet is dismissed (close button / overlay / Esc).
 *
 * Variant data comes from Shopify's public product JSON (/products/<handle>.js).
 * Cart adds go through the theme's CartAPI (POST /cart/add.js with multiple
 * items) which dispatches cart:item-added + cart:updated — the same events the
 * theme's own add-to-cart uses, so the cart drawer refreshes and opens
 * identically (cart-drawer.js listens for both).
 *
 * Builds on the theme's shipped primitives with graceful fallbacks:
 *   - ui-sheet shell markup + window.UISheet (open/close, overlay, Esc)
 *   - window.UntamedSheets.renderVariantPicker (chip markup, same look as PDP
 *     quick-view chips)
 *   - window.CartAPI / window.formatMoney
 */
(function () {
  'use strict';

  if (window.HealixVariantSheet) return;

  var SHEET_ID = 'healix-variant-sheet';
  var dom = null;          // { overlay, sheet, body, footer, cta, error }
  var session = null;      // active open() session
  var productCache = {};
  var productInflight = {};
  var kiwiChartCache = {};
  var kiwiAppReady = null;

  /* ------------------------------------------------------------------ */
  /* Utilities                                                            */
  /* ------------------------------------------------------------------ */

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c];
    });
  }

  function money(cents) {
    if (typeof window.formatMoney === 'function') {
      try { return window.formatMoney(cents, window.themeMoneyFormat); } catch (e) { /* fall through */ }
    }
    if (typeof window.formatMoneyFallback === 'function') {
      try { return window.formatMoneyFallback(cents); } catch (e) { /* fall through */ }
    }
    return '$' + ((Number(cents) || 0) / 100).toFixed(2);
  }

  function sizedImage(url, size) {
    if (!url) return '';
    return String(url).replace(/(\.(?:jpg|jpeg|png|gif|webp))(\?.*)?$/i, '_' + size + '$1$2');
  }

  function paintIcons(scope) {
    if (window.UntamedIcons && window.UntamedIcons.refresh) window.UntamedIcons.refresh(scope || (dom && dom.sheet));
  }

  function rootUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.endsWith('/') ? root : root + '/';
  }

  function previewQuery() {
    try {
      var previewId = new URLSearchParams(window.location.search).get('preview_theme_id');
      return previewId ? '?preview_theme_id=' + encodeURIComponent(previewId) : '';
    } catch (e) {
      return '';
    }
  }

  /* ------------------------------------------------------------------ */
  /* Product JSON                                                         */
  /* ------------------------------------------------------------------ */

  function fetchProduct(handle) {
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    if (productInflight[handle]) return productInflight[handle];
    productInflight[handle] = fetch(rootUrl() + 'products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (p) { productCache[handle] = p; delete productInflight[handle]; return p; })
      .catch(function (e) { delete productInflight[handle]; throw e; });
    return productInflight[handle];
  }

  function isDefaultOnly(product) {
    return product.options.length === 1 &&
      product.options[0].name === 'Title' &&
      (product.options[0].values || []).length === 1 &&
      product.options[0].values[0] === 'Default Title';
  }

  // A value is selectable if some available variant matches it plus every
  // OTHER already-chosen option (null = wildcard). Mirrors quick-view / the
  // cart drawer's upsell resolver so cross-option availability feels the same.
  function valueAvailable(product, chosen, optIdx, value) {
    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i];
      if (!v.available) continue;
      if (String(v.options[optIdx]) !== String(value)) continue;
      var ok = true;
      for (var k = 0; k < product.options.length; k++) {
        if (k === optIdx) continue;
        if (chosen[k] != null && String(v.options[k]) !== String(chosen[k])) { ok = false; break; }
      }
      if (ok) return true;
    }
    return false;
  }

  function resolveVariant(product, chosen) {
    for (var k = 0; k < chosen.length; k++) { if (chosen[k] == null) return null; }
    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i], ok = true;
      for (var j = 0; j < chosen.length; j++) {
        if (String(v.options[j]) !== String(chosen[j])) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  }

  /* ------------------------------------------------------------------ */
  /* Chip markup — prefer the theme's shared renderer so chips match the  */
  /* PDP/quick-view exactly; keep a same-classes fallback.                */
  /* ------------------------------------------------------------------ */

  function renderGroupsFallback(blocks) {
    return blocks.map(function (block) {
      return block.options.map(function (opt) {
        var values = opt.values.map(function (v) {
          var cls = (v.selected ? ' is-selected' : '') + (v.available ? '' : ' is-unavailable');
          return '<button type="button" class="vpick__pill' + cls + '"' +
            ' data-vp-pi="' + block.pi + '" data-vp-oi="' + opt.oi + '" data-vp-val="' + esc(v.value) + '"' +
            (v.available ? '' : ' aria-disabled="true"') + '>' + esc(v.value) + '</button>';
        }).join('');
        return '<div class="vpick__group"><div class="vpick__label">' + esc(opt.name) +
          '</div><div class="vpick__values">' + values + '</div></div>';
      }).join('');
    }).join('');
  }

  function renderGroups(blocks) {
    if (window.UntamedSheets && typeof window.UntamedSheets.renderVariantPicker === 'function') {
      return window.UntamedSheets.renderVariantPicker(blocks, { bare: true });
    }
    return renderGroupsFallback(blocks);
  }

  /* ------------------------------------------------------------------ */
  /* Shell (built lazily on first open; ui-sheet contract markup)         */
  /* ------------------------------------------------------------------ */

  var CSS =
    '.ui-sheet--hvs{--ui-sheet-max-width:480px}' +
    '.ui-sheet--hvs .ui-sheet__body{flex:1 1 auto;min-height:0}' +
    // Slim header — scoped to THIS sheet only (the shared ui-sheet header is
    // too tall for a picker): tighter padding, smaller single-line title,
    // compact close button vertically centered with the text.
    '.ui-sheet--hvs .ui-sheet__header{padding:12px 16px 10px;align-items:center}' +
    '.ui-sheet--hvs .ui-sheet__title{font-size:15px;font-weight:600;line-height:1.2;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}' +
    '.ui-sheet--hvs .ui-sheet__close{width:30px;height:30px}' +
    '.ui-sheet--hvs .ui-sheet__close .ph-bold{width:18px;height:18px;font-size:18px}' +
    '@media (max-width:749.98px){' +
      '.ui-sheet--hvs{top:auto;bottom:0;height:auto;max-height:80vh;max-height:80dvh;border-radius:16px 16px 0 0}' +
    '}' +
    '.hvs__item{display:flex;gap:14px;padding:18px 0;border-top:1px solid var(--color-border-light,#eee)}' +
    '.hvs__item:first-child{border-top:none;padding-top:0}' +
    '.hvs__item.is-removed{display:none}' +
    '.hvs__thumb{width:64px;height:64px;border-radius:var(--radius-lg,8px);object-fit:cover;flex-shrink:0;background:var(--color-fill-light,#f5f5f5)}' +
    '.hvs__main{flex:1;min-width:0}' +
    '.hvs__head{display:flex;align-items:flex-start;gap:10px;margin-bottom:12px}' +
    '.hvs__meta{flex:1;min-width:0}' +
    '.hvs__title{font-family:var(--font-heading--family,inherit);font-size:var(--font-size-base,16px);font-weight:500;color:var(--color-foreground,var(--color-text-primary,#1a1a1a));margin:0}' +
    '.hvs__remove{flex-shrink:0;display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;padding:0;margin:-2px -2px 0 0;border:none;border-radius:var(--radius-full,999px);background:transparent;color:var(--color-text-secondary,#767676);cursor:pointer;transition:background-color .15s ease,color .15s ease}' +
    '.hvs__remove:hover{background:var(--color-fill-light,#f5f5f5);color:var(--color-text-primary,#1a1a1a)}' +
    '.hvs__remove .ph-bold{font-size:16px;line-height:1}' +
    '.hvs__item-error{font-size:13px;color:var(--color-text-secondary,#767676);margin:4px 0 0}' +
    '.hvs__empty,.hvs__loading{padding:28px 0;text-align:center;font-size:14px;color:var(--color-text-secondary,#767676)}' +
    '.hvs__spinner{display:inline-block;width:22px;height:22px;margin-bottom:10px;border:2px solid var(--color-border-default,#ddd);border-top-color:var(--color-text-primary,#1a1a1a);border-radius:50%;animation:hvs-spin .7s linear infinite}' +
    '@keyframes hvs-spin{to{transform:rotate(360deg)}}' +
    '.hvs__error{margin:10px 0 0;font-size:13px;color:var(--cart-sale,#c63a2a);text-align:center}' +
    '.hvs__error[hidden]{display:none}' +
    '.hvs__main .vpick__group .vpick__label{display:flex;align-items:center;justify-content:space-between;gap:12px;width:100%;}' +
    '.hvs__kiwi-size-chart{display:inline-flex;align-items:center;flex-shrink:0;gap:5px;padding:0;margin:0;border:0;background:none;font-family:inherit;font-size:inherit;font-weight:inherit;line-height:inherit;color:var(--color-text-secondary,#707070);text-decoration:underline;text-underline-offset:3px;cursor:pointer;opacity:1;pointer-events:auto;transition:color .15s ease}' +
    '.hvs__kiwi-size-chart[hidden]{display:none!important}' +
    '.hvs__kiwi-size-chart:hover{color:var(--color-text-primary,#111)}' +
    '.hvs__kiwi-size-chart .ph-bold{font-size:1em;line-height:inherit}' +
    '.hvs__cta .hvs__cta-price{font-weight:600;opacity:.85}' +
    '.hvs__cta .hvs__cta-price::before{content:"\\00b7";margin:0 8px;opacity:.6}';

  function injectCss() {
    if (document.getElementById('hvs-styles')) return;
    // If a page links the canonical stylesheet (assets/healix-variant-sheet.css
    // — same rules as CSS above), don't double up.
    if (document.querySelector('link[href*="healix-variant-sheet.css"]')) return;
    var style = document.createElement('style');
    style.id = 'hvs-styles';
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  function build() {
    if (dom) return dom;
    injectCss();

    var overlay = document.createElement('div');
    overlay.className = 'ui-sheet-overlay';
    overlay.setAttribute('data-ui-sheet-overlay', SHEET_ID);
    overlay.setAttribute('aria-hidden', 'true');

    var sheet = document.createElement('div');
    sheet.className = 'ui-sheet ui-sheet--hvs';
    sheet.setAttribute('data-ui-sheet', SHEET_ID);
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-modal', 'true');
    sheet.setAttribute('aria-hidden', 'true');
    sheet.setAttribute('aria-label', 'Choose your options');
    sheet.innerHTML =
      '<div class="ui-sheet__header">' +
        '<h3 class="ui-sheet__title" data-ui-sheet-title>Choose your options</h3>' +
        '<button type="button" class="ui-sheet__close" data-ui-sheet-close aria-label="Close">' +
          '<span class="ph-bold ph-x"></span>' +
        '</button>' +
      '</div>' +
      '<div class="ui-sheet__body" data-hvs-body></div>' +
      '<div class="ui-sheet__footer" data-hvs-footer>' +
        '<button type="button" class="ui-sheet__btn ui-sheet__btn--primary ui-sheet__btn--block hvs__cta" data-hvs-add disabled></button>' +
        '<div class="hvs__error" data-hvs-error hidden></div>' +
      '</div>';

    document.body.appendChild(overlay);
    document.body.appendChild(sheet);

    dom = {
      overlay: overlay,
      sheet: sheet,
      body: sheet.querySelector('[data-hvs-body]'),
      cta: sheet.querySelector('[data-hvs-add]'),
      error: sheet.querySelector('[data-hvs-error]')
    };

    // Focus trap (ui-sheet.js moves initial focus but doesn't trap Tab).
    sheet.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || sheet.getAttribute('aria-hidden') !== 'false') return;
      var focusables = sheet.querySelectorAll(
        'button:not([disabled]):not([hidden]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && (document.activeElement === first || !sheet.contains(document.activeElement))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    // Chip selection + per-item remove (delegated so re-renders keep working).
    dom.body.addEventListener('click', function (e) {
      var chart = e.target.closest('[data-hvs-kiwi-size-chart]');
      if (chart) {
        e.preventDefault();
        e.stopPropagation();
        openKiwiSizeChart(chart.getAttribute('data-hvs-product-handle'), chart);
        return;
      }
      var chip = e.target.closest('[data-vp-val]');
      if (chip) {
        if (chip.getAttribute('aria-disabled') === 'true') return;
        if (!session) return;
        var pi = parseInt(chip.getAttribute('data-vp-pi'), 10);
        var oi = parseInt(chip.getAttribute('data-vp-oi'), 10);
        var val = chip.getAttribute('data-vp-val');
        var entry = session.entries[pi];
        if (!entry || entry.removed || !entry.product) return;
        entry.chosen[oi] = String(entry.chosen[oi]) === String(val) ? null : val;
        renderEntry(entry);
        updateCta();
        return;
      }
      var remove = e.target.closest('[data-hvs-remove]');
      if (remove && session) {
        var rpi = parseInt(remove.getAttribute('data-hvs-remove'), 10);
        var rEntry = session.entries[rpi];
        if (!rEntry) return;
        rEntry.removed = true;
        var el = dom.body.querySelector('[data-hvs-item="' + rpi + '"]');
        if (el) el.classList.add('is-removed');
        if (activeEntries().length === 0) {
          dom.body.innerHTML = '<div class="hvs__empty">All items were removed.</div>';
        }
        updateCta();
      }
    });

    dom.cta.addEventListener('click', addToCart);

    // Dismissal (close button / overlay / Esc are handled by ui-sheet.js;
    // we settle the pending promise when OUR sheet closes).
    document.addEventListener('ui-sheet:close', function (e) {
      if (!e.detail || e.detail.id !== SHEET_ID) return;
      settle({ added: [] });
    });

    // ui-sheet.js also listens for Escape. While Kiwi owns the top modal,
    // route Escape to Kiwi's own close control so the set picker underneath
    // remains open for the shopper's size selection.
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !document.documentElement.classList.contains('kiwi-sizing-modal-visible')) return;
      var kiwiClose = document.querySelector('.kiwi-sizing-modal .ks-modal-close');
      if (!kiwiClose) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      kiwiClose.click();
    }, true);

    return dom;
  }

  /* ------------------------------------------------------------------ */
  /* Session / rendering                                                  */
  /* ------------------------------------------------------------------ */

  function activeEntries() {
    return session ? session.entries.filter(function (en) { return !en.removed && en.product; }) : [];
  }

  function settle(result) {
    if (!session || session.settled) return;
    session.settled = true;
    var resolve = session.resolve;
    session = null;
    resolve(result);
  }

  function sheetOpen(heading) {
    var title = dom.sheet.querySelector('[data-ui-sheet-title]');
    if (title) title.textContent = heading;
    dom.sheet.setAttribute('aria-label', heading);
    if (window.UISheet) {
      window.UISheet.open(SHEET_ID);
    } else {
      dom.overlay.setAttribute('aria-hidden', 'false');
      dom.sheet.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('ui-sheet-open');
    }
  }

  function sheetClose() {
    if (window.UISheet && window.UISheet.current && window.UISheet.current() === SHEET_ID) {
      window.UISheet.close();
    } else {
      dom.overlay.setAttribute('aria-hidden', 'true');
      dom.sheet.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('ui-sheet-open');
      settle({ added: [] });
    }
  }

  // Per-item prices are intentionally NOT displayed: callers pass item.price in
  // unknown units (the complementary API sends dollar strings like "39.99"
  // while formatMoney expects integer cents), so any per-item figure is
  // display-unsafe. The CTA total is the only price shown — computed from the
  // fetched /products/<handle>.js variant prices (integer cents, correct).

  function entryBlocks(entry) {
    var p = entry.product;
    if (isDefaultOnly(p)) return [];
    var options = p.options.map(function (opt, idx) {
      return {
        name: opt.name,
        oi: idx,
        isColor: false, // colorways are separate products in this store
        selected: null,
        values: (opt.values || []).map(function (val) {
          return {
            value: val,
            selected: String(entry.chosen[idx]) === String(val),
            available: valueAvailable(p, entry.chosen, idx, val),
            hex: null
          };
        })
      };
    });
    return [{ pi: entry.pi, options: options }];
  }

  // Kiwi initializes the PDP product automatically. Complete-the-set items can
  // be different products, so each gets a hidden top-level Kiwi injection host
  // populated with that item's product data. Clicking our visible action then
  // clicks Kiwi's generated trigger: Kiwi remains the sole modal owner.
  function hasSizeOption(product) {
    return !!(product && Array.isArray(product.options) && product.options.some(function (opt) {
      return String(opt.name || '').trim().toLowerCase() === 'size';
    }));
  }

  function sizeChartAction(entry) {
    if (!hasSizeOption(entry.product)) return '';
    var title = entry.item.title || entry.product.title || entry.item.handle;
    return '<button type="button" class="product-size-chart-link hvs__kiwi-size-chart" data-hvs-kiwi-size-chart hidden' +
      ' data-hvs-product-handle="' + esc(entry.item.handle) + '"' +
      ' aria-label="Open Kiwi Size Chart for ' + esc(title) + '">' +
      '<span class="ph-bold ph-ruler" aria-hidden="true"></span>Size Chart</button>';
  }

  function findKiwiTrigger(scope) {
    if (!scope) return null;
    return scope.querySelector(
      '.sizing-chart-modal-link, .ks-chart-modal-link, .sizing-chart-modal-button, .ks-chart-modal-button'
    );
  }

  function waitForKiwiApp() {
    if (window.ks && typeof window.ks.loadSizing === 'function') return Promise.resolve(window.ks);
    if (kiwiAppReady) return kiwiAppReady;
    kiwiAppReady = new Promise(function (resolve, reject) {
      var shop = (window.Shopify && window.Shopify.shop) || 'm1jd1u-jj.myshopify.com';
      var script = document.querySelector('script[src*="SizingPlugin.prod.js"]');
      var started = Date.now();

      // The regular PDP gets Kiwi through its app block. Complete-the-set
      // opens from the Flash Deals page do not have that block, so load the
      // same first-party plugin on demand instead of waiting forever for a
      // global that can never be created.
      if (!script) {
        script = document.createElement('script');
        script.async = true;
        script.src = 'https://app.kiwisizing.com/web/js/dist/kiwiSizing/plugin/SizingPlugin.prod.js?v=339&shop=' +
          encodeURIComponent(String(shop || ''));
        (document.head || document.documentElement).appendChild(script);
      }

      (function check() {
        if (window.ks && typeof window.ks.loadSizing === 'function') { resolve(window.ks); return; }
        if (Date.now() - started >= 15000) { reject(new Error('Kiwi did not initialize')); return; }
        setTimeout(check, 100);
      })();
    }).catch(function (error) {
      kiwiAppReady = null;
      throw error;
    });
    return kiwiAppReady;
  }

  function extractKiwiCollections(handle) {
    return fetch(rootUrl() + 'products/' + encodeURIComponent(handle) + previewQuery(), {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' }
    }).then(function (response) {
      if (!response.ok) throw new Error('Product page returned ' + response.status);
      return response.text();
    }).then(function (html) {
      var match = html.match(/KiwiSizing\.data\s*=\s*\{[\s\S]*?\bcollections:\s*"([^"]*)"/);
      return match ? match[1] : '';
    });
  }

  function waitForKiwiTrigger(host) {
    var existing = findKiwiTrigger(host);
    if (existing) return Promise.resolve(existing);
    return new Promise(function (resolve, reject) {
      var finished = false;
      var observer = new MutationObserver(function () {
        var trigger = findKiwiTrigger(host);
        if (!trigger || finished) return;
        finished = true;
        observer.disconnect();
        clearTimeout(timer);
        resolve(trigger);
      });
      var timer = setTimeout(function () {
        if (finished) return;
        finished = true;
        observer.disconnect();
        reject(new Error('No Kiwi chart matched this product'));
      }, 15000);
      observer.observe(host, { childList: true, subtree: true });
    });
  }

  function kiwiProductData(product, collections) {
    var current = window.KiwiSizing && window.KiwiSizing.data;
    return {
      collections: collections || '',
      tags: Array.isArray(product.tags) ? product.tags.join(',') : String(product.tags || ''),
      product: String(product.id),
      metafields: [],
      vendor: product.vendor || '',
      type: product.type || '',
      title: product.title || '',
      images: product.images || [],
      options: product.options || [],
      variants: (product.variants || []).map(function (variant) {
        return { id: variant.id, title: variant.title, available: variant.available };
      }),
      shopCurrency: current && current.shopCurrency ? current.shopCurrency : undefined
    };
  }

  function prepareKiwiSizeChart(handle, product) {
    var cached = kiwiChartCache[handle];
    if (cached && cached.trigger && cached.trigger.isConnected) return Promise.resolve(cached.trigger);
    if (cached && cached.promise) return cached.promise;

    cached = kiwiChartCache[handle] = { host: null, trigger: null, promise: null };
    cached.promise = Promise.all([waitForKiwiApp(), extractKiwiCollections(handle)]).then(function (results) {
      var host = document.createElement('div');
      // Kiwi will not reliably inject or open a trigger inside display:none.
      // Keep the host rendered but visually/off-screen hidden; Kiwi owns the
      // actual top-level chart modal when the generated trigger is clicked.
      host.hidden = false;
      host.setAttribute('aria-hidden', 'true');
      if (host.style) {
        host.style.cssText = 'position:fixed;left:-10000px;top:0;width:1px;height:1px;overflow:hidden;opacity:0;pointer-events:none;';
      }
      host.setAttribute('data-hvs-kiwi-host', handle);
      document.body.appendChild(host);
      cached.host = host;

      var triggerPromise = waitForKiwiTrigger(host);
      window.ks.loadSizing({
        productData: kiwiProductData(product, results[1]),
        options: { el: host, injectionOrder: 3 }
      });
      return triggerPromise;
    }).then(function (nativeTrigger) {
      cached.trigger = nativeTrigger;
      return nativeTrigger;
    }).catch(function (error) {
      if (cached.host && cached.host.parentNode) cached.host.parentNode.removeChild(cached.host);
      delete kiwiChartCache[handle];
      throw error;
    });
    return cached.promise;
  }

  // Start Kiwi's sizing request as soon as the mini-PDP is rendered. The
  // normal product page preloads this through its app block; Flash Deals does
  // not, so waiting until the shopper clicks would add the plugin load,
  // collection lookup, and chart request to the click itself.
  function preloadKiwiSizeChart(handle, suppliedProduct) {
    if (!handle) return Promise.resolve(false);
    var productPromise = suppliedProduct ? Promise.resolve(suppliedProduct) : fetchProduct(handle);
    return productPromise.then(function (product) {
      return prepareKiwiSizeChart(handle, product);
    }).then(function () { return true; });
  }

  function openKiwiSizeChart(handle, trigger, suppliedProduct) {
    if (!handle || !trigger) return Promise.resolve(false);
    trigger.setAttribute('aria-busy', 'true');
    var entry = session && session.entries.filter(function (candidate) {
      return candidate.item.handle === handle;
    })[0];
    var product = suppliedProduct || (entry && entry.product);
    return preloadKiwiSizeChart(handle, product).then(function () {
      return kiwiChartCache[handle] && kiwiChartCache[handle].trigger;
    }).then(function (nativeTrigger) {
      if (!nativeTrigger) throw new Error('No Kiwi chart trigger matched this product');
      trigger.removeAttribute('aria-busy');
      nativeTrigger.click();
      return true;
    }).catch(function (error) {
      trigger.removeAttribute('aria-busy');
      if (window.console && console.warn) console.warn('[HealixVariantSheet] Kiwi chart load failed', error);
      return false;
    });
  }

  function mountSizeChartAction(host, entry) {
    if (!hasSizeOption(entry.product)) return;
    var sizeIndex = -1;
    entry.product.options.some(function (opt, index) {
      if (String(opt.name || '').trim().toLowerCase() === 'size') {
        sizeIndex = index;
        return true;
      }
      return false;
    });
    if (sizeIndex < 0) return;
    var sizeChip = host.querySelector('[data-vp-pi="' + entry.pi + '"][data-vp-oi="' + sizeIndex + '"]');
    var group = sizeChip && sizeChip.closest('.vpick__group');
    var label = group && group.querySelector('.vpick__label');
    if (label && !label.querySelector('[data-hvs-kiwi-size-chart]')) {
      label.insertAdjacentHTML('beforeend', sizeChartAction(entry));
      var action = label.querySelector('[data-hvs-kiwi-size-chart]');
      prepareKiwiSizeChart(entry.item.handle, entry.product).then(function () {
        if (action && action.isConnected) action.hidden = false;
      }).catch(function () {
        if (action && action.parentNode) action.remove();
      });
    }
  }

  function renderEntry(entry) {
    var host = dom.body.querySelector('[data-hvs-pickers="' + entry.pi + '"]');
    if (host) {
      host.innerHTML = renderGroups(entryBlocks(entry));
      mountSizeChartAction(host, entry);
    }
  }

  function renderAll() {
    var multi = session.multi;
    dom.body.innerHTML = session.entries.map(function (entry) {
      if (entry.failed) {
        return '<section class="hvs__item" data-hvs-item="' + entry.pi + '">' +
          '<div class="hvs__main"><div class="hvs__head"><div class="hvs__meta">' +
          '<h4 class="hvs__title">' + esc(entry.item.title || entry.item.handle) + '</h4>' +
          '<p class="hvs__item-error">Couldn\u2019t load this item \u2014 it won\u2019t be added.</p>' +
          '</div></div></div></section>';
      }
      var p = entry.product;
      var img = entry.item.imageUrl || (p.featured_image || (p.images && p.images[0]) || '');
      return '<section class="hvs__item' + (entry.removed ? ' is-removed' : '') + '" data-hvs-item="' + entry.pi + '">' +
        (img ? '<img class="hvs__thumb" src="' + esc(sizedImage(img, '128x128')) + '" alt="" loading="lazy">' : '') +
        '<div class="hvs__main">' +
          '<div class="hvs__head">' +
            '<div class="hvs__meta">' +
              '<h4 class="hvs__title">' + esc(entry.item.title || p.title) + '</h4>' +
            '</div>' +
            (multi
              ? '<button type="button" class="hvs__remove" data-hvs-remove="' + entry.pi + '" aria-label="Remove ' + esc(entry.item.title || p.title) + ' from set"><span class="ph-bold ph-x"></span></button>'
              : '') +
          '</div>' +
          '<div data-hvs-pickers="' + entry.pi + '"></div>' +
        '</div>' +
      '</section>';
    }).join('');

    session.entries.forEach(function (entry) {
      if (!entry.failed) renderEntry(entry);
    });
    paintIcons();
    updateCta();
  }

  function updateCta() {
    if (!session || !dom) return;
    var active = activeEntries();
    var allResolved = active.length > 0;
    var total = 0;
    var anySelected = false;
    active.forEach(function (entry) {
      var v = resolveVariant(entry.product, entry.chosen);
      if (v) { total += v.price; anySelected = true; }
      else allResolved = false;
    });

    var base = session.multi ? 'Add set to cart' : 'Add to cart';
    dom.cta.innerHTML = esc(base) + (anySelected ? '<span class="hvs__cta-price">' + money(total) + '</span>' : '');
    dom.cta.disabled = !allResolved || session.adding;
  }

  /* ------------------------------------------------------------------ */
  /* Cart add                                                             */
  /* ------------------------------------------------------------------ */

  function postCartAdd(items) {
    // Prefer the theme's CartAPI: it POSTs /cart/add.js and dispatches
    // cart:item-added + cart:updated — cart-drawer.js re-renders on
    // cart:updated and opens itself on cart:item-added (open-on-add),
    // exactly like the theme's own add-to-cart flows.
    if (window.CartAPI && typeof window.CartAPI.addItems === 'function') {
      return window.CartAPI.addItems(items);
    }
    // Fallback: same request + same events by hand.
    return fetch(rootUrl() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) throw new Error(data.description || data.message || 'Failed to add items to cart');
        return fetch(rootUrl() + 'cart.js').then(function (cr) { return cr.json(); }).then(function (cart) {
          var first = (data.items && data.items[0]) || {};
          window.dispatchEvent(new CustomEvent('cart:item-added', { detail: { item: first, cart: cart } }));
          window.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
          return data;
        });
      });
    });
  }

  function addToCart() {
    if (!session || session.adding) return;
    var active = activeEntries();
    var lines = [];
    var added = [];
    for (var i = 0; i < active.length; i++) {
      var v = resolveVariant(active[i].product, active[i].chosen);
      if (!v) return; // CTA should be disabled; guard anyway
      lines.push({ id: v.id, quantity: 1 });
      added.push({ handle: active[i].item.handle, variantId: v.id, quantity: 1 });
    }
    if (!lines.length) return;

    session.adding = true;
    dom.error.hidden = true;
    dom.cta.disabled = true;
    dom.cta.textContent = 'Adding\u2026';

    var current = session;
    postCartAdd(lines).then(function () {
      if (session !== current) return;
      session.settled = true; // settle BEFORE close so ui-sheet:close doesn't resolve {added:[]}
      var resolve = session.resolve;
      session = null;
      if (window.UISheet && window.UISheet.current && window.UISheet.current() === SHEET_ID) {
        window.UISheet.close();
      } else {
        dom.overlay.setAttribute('aria-hidden', 'true');
        dom.sheet.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.remove('ui-sheet-open');
      }
      resolve({ added: added });
    }).catch(function (e) {
      if (session !== current) return;
      session.adding = false;
      dom.error.hidden = false;
      dom.error.textContent = (e && e.message) || 'Could not add to cart. Please try again.';
      updateCta();
    });
  }

  /* ------------------------------------------------------------------ */
  /* Public API                                                           */
  /* ------------------------------------------------------------------ */

  function open(opts) {
    opts = opts || {};
    var items = Array.isArray(opts.items) ? opts.items.filter(function (it) { return it && it.handle; }) : [];

    return new Promise(function (resolve) {
      if (!items.length) { resolve({ added: [] }); return; }

      build();
      if (session) settle({ added: [] }); // supersede any previous session

      session = {
        entries: items.map(function (item, i) {
          return { pi: i, item: item, product: null, chosen: null, removed: false, failed: false };
        }),
        multi: items.length > 1,
        heading: opts.heading || (items.length > 1 ? 'Complete the set' : 'Choose your options'),
        resolve: resolve,
        settled: false,
        adding: false
      };

      dom.error.hidden = true;
      dom.cta.disabled = true;
      dom.cta.textContent = session.multi ? 'Add set to cart' : 'Add to cart';
      dom.body.innerHTML = '<div class="hvs__loading"><span class="hvs__spinner" aria-hidden="true"></span><br>Loading options\u2026</div>';

      sheetOpen(session.heading);

      var current = session;
      Promise.all(items.map(function (item) {
        return fetchProduct(item.handle).then(function (p) { return { ok: true, product: p }; },
          function () { return { ok: false }; });
      })).then(function (results) {
        if (session !== current || session.settled) return;
        var anyOk = false;
        results.forEach(function (res, i) {
          var entry = session.entries[i];
          if (!res.ok) { entry.failed = true; entry.removed = true; return; }
          anyOk = true;
          entry.product = res.product;
          // Nothing preselected — every option is an explicit choice — except
          // single-value options and Default Title products (auto-resolved).
          entry.chosen = res.product.options.map(function (opt) {
            var values = opt.values || [];
            return values.length === 1 ? values[0] : null;
          });
        });
        if (!anyOk) {
          dom.body.innerHTML = '<div class="hvs__empty">Couldn\u2019t load these items. Please try again.</div>';
          return;
        }
        renderAll();
      });
    });
  }

  // A product form can request Kiwi directly without opening this variant sheet.
  // Kiwi owns the resulting top-level dialog, preventing nested modal focus and
  // clipping problems in the Flash Deals mini-PDP.
  window.HealixVariantSheet = {
    open: open,
    openKiwiSizeChart: openKiwiSizeChart,
    preloadKiwiSizeChart: preloadKiwiSizeChart
  };
})();
